import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Button, Table, Row, Col, Badge, Dropdown, Nav, Form } from 'react-bootstrap';
import { Search, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useStock,
  OutboundStatus,
  type GetStockHistoryResponse,
  type InboundStockRequest,
  type OutboundStockRequest,
  type ProductLookupResponse,
} from '../../../../apps/hooks/stock/useStock';
import { toast } from 'react-hot-toast';
import { ROUTES } from '../../../../routes/routes';
import { useAuth } from '../../../../providers/AuthProvider';

const SEARCH_TYPES = [
  { value: 'sku', label: 'SKU' },
  { value: 'productId', label: 'Product ID' },
];

export default function WMSStockPage() {
  const [searchBy, setSearchBy] = useState('sku');
  const [searchValue, setSearchValue] = useState('');
  const [stockData, setStockData] = useState<GetStockHistoryResponse | null>(null);
  const [productInfo, setProductInfo] = useState<ProductLookupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState<'search' | 'inbound' | 'outbound'>('search');
  const [operationLoading, setOperationLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const stockHook = useStock();

  // Inbound form states
  const [inboundForm, setInboundForm] = useState<Omit<InboundStockRequest, 'productId'> & { sku: string }>({
    sku: '',
    qty: 0,
    trackingNumber: '',
    carrier: '',
    locationId: 'WH#00',
    orderId: '',
    auditor: '',
  });

  // Outbound form states
  const [outboundForm, setOutboundForm] = useState<Omit<OutboundStockRequest, 'productId'> & { sku: string }>({
    sku: '',
    qty: 0,
    trackingNumber: '',
    carrier: '',
    locationId: 'WH#00',
    status: OutboundStatus.PENDING,
    orderId: '',
    auditor: '',
  });

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      toast.error(
        `Please enter a ${SEARCH_TYPES.find((type) => type.value === searchBy)?.label.toLowerCase() || 'search value'}`,
      );
      return;
    }

    let searchParams: { productId?: number; sku?: string };

    if (searchBy === 'productId') {
      const numericProductId = parseInt(searchValue.trim(), 10);
      if (isNaN(numericProductId)) {
        toast.error('Product ID must be a valid number');
        return;
      }
      searchParams = { productId: numericProductId };
    } else {
      searchParams = { sku: searchValue.trim() };
    }

    setLoading(true);
    setHasSearched(true);

    try {
      // Fetch stock history
      const result = await stockHook.getStockHistory(searchParams);
      console.log('Stock API Response:', result);
      setStockData(result);

      // Fetch product info if searching by SKU
      if (searchBy === 'sku') {
        try {
          const productResult = await stockHook.getProductBySku(searchValue.trim());
          console.log('Product API Response:', productResult);
          setProductInfo(productResult);
        } catch (productError) {
          console.warn('Failed to fetch product info:', productError);
          setProductInfo(null);
        }
      } else {
        setProductInfo(null);
      }

      if (!result.events || result.events.length === 0) {
        toast.error(
          `No stock history found for this ${SEARCH_TYPES.find((type) => type.value === searchBy)?.label.toLowerCase()}`,
        );
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(`Failed to get stock history: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setStockData(null);
      setProductInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleOrderClick = (orderId: string) => {
    navigate(ROUTES.oms.orderDetail(orderId));
  };

  const handleInboundSubmit = async () => {
    if (!inboundForm.sku.trim() || !inboundForm.qty) {
      toast.error('SKU and Quantity are required');
      return;
    }

    setOperationLoading(true);
    try {
      // First, lookup productId by SKU
      const productLookup = await stockHook.getProductBySku(inboundForm.sku.trim());

      // Then perform the inbound operation
      const inboundRequest: InboundStockRequest = {
        productId: productLookup.productId,
        qty: inboundForm.qty,
        locationId: 'WH#00',
        auditor: user?.name || user?.email || 'unknown-user',
      };

      if (inboundForm.trackingNumber?.trim()) {
        inboundRequest.trackingNumber = inboundForm.trackingNumber.trim();
      }
      if (inboundForm.carrier?.trim()) {
        inboundRequest.carrier = inboundForm.carrier.trim();
      }
      if (inboundForm.orderId?.trim()) {
        inboundRequest.orderId = inboundForm.orderId.trim();
      }

      const result = await stockHook.inboundStock(inboundRequest);

      if (result.success) {
        // Reset form
        setInboundForm({
          sku: '',
          qty: 0,
          trackingNumber: '',
          carrier: '',
          locationId: 'WH#00',
          orderId: '',
          auditor: '',
        });
      } else {
        toast.error(`Inbound failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Inbound error:', error);
      toast.error(`Inbound operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleOutboundSubmit = async () => {
    if (!outboundForm.sku.trim() || !outboundForm.qty) {
      toast.error('SKU and Quantity are required');
      return;
    }

    setOperationLoading(true);
    try {
      // First, lookup productId by SKU
      const productLookup = await stockHook.getProductBySku(outboundForm.sku.trim());

      // Then perform the outbound operation
      const outboundRequest: OutboundStockRequest = {
        productId: productLookup.productId,
        qty: outboundForm.qty,
        locationId: 'WH#00',
        auditor: user?.name || user?.email || 'unknown-user',
      };

      if (outboundForm.trackingNumber?.trim()) {
        outboundRequest.trackingNumber = outboundForm.trackingNumber.trim();
      }
      if (outboundForm.carrier?.trim()) {
        outboundRequest.carrier = outboundForm.carrier.trim();
      }
      if (outboundForm.status) {
        outboundRequest.status = outboundForm.status;
      }
      if (outboundForm.orderId?.trim()) {
        outboundRequest.orderId = outboundForm.orderId.trim();
      }

      const result = await stockHook.outboundStock(outboundRequest);

      if (result.success) {
        // Reset form
        setOutboundForm({
          sku: '',
          qty: 0,
          trackingNumber: '',
          carrier: '',
          locationId: 'WH#00',
          status: OutboundStatus.PENDING,
          orderId: '',
          auditor: '',
        });
      } else {
        toast.error(`Outbound failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Outbound error:', error);
      toast.error(`Outbound operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setOperationLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getTypeBadgeVariant = (type: string) => {
    return type === 'INBOUND' ? 'info' : 'warning';
  };

  const formatDateTime = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('ko-KR');
    } catch {
      return dateString;
    }
  };

  const handleExportStockStatus = async () => {
    setExportLoading(true);
    try {
      const blob = await stockHook.exportStockStatus();

      // Convert blob to JSON to get the data
      const text = await blob.text();
      const data = JSON.parse(text);

      // Convert to CSV
      if (!data.stocks || data.stocks.length === 0) {
        toast.error('No stock data available to export');
        return;
      }

      // Create CSV headers
      const headers = [
        'Product ID',
        'SKU',
        'Total Quantity',
        'Total Reserved Quantity',
        'Available Quantity',
        'Locations',
        'Updated At',
      ];

      // Create CSV rows
      const rows = data.stocks.map((stock: any) => {
        const availableQty = stock.totalQuantity - stock.totalReservedQuantity;
        const locations = Object.entries(stock.locationQuantities || {})
          .map(([loc, qty]) => `${loc}:${qty}`)
          .join('; ');

        return [
          stock.productId,
          stock.sku || '',
          stock.totalQuantity,
          stock.totalReservedQuantity,
          availableQty,
          locations,
          stock.updatedAt,
        ];
      });

      // Combine headers and rows
      // @ts-ignore
      const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

      // Create a Blob and download
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(csvBlob);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.href = url;
      link.download = `stock_status_${timestamp}.csv`;
      link.click();

      URL.revokeObjectURL(url);
      toast.success('Stock status exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export stock status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">WMS - Stock History</h1>
        <Button
          variant="success"
          onClick={handleExportStockStatus}
          disabled={exportLoading}
          className="d-flex align-items-center"
          style={{ gap: '8px' }}
        >
          <Download style={{ width: '16px', height: '16px' }} />
          {exportLoading ? 'Exporting...' : 'Export Stock Status'}
        </Button>
      </div>

      {/* Search Section */}
      <Card>
        <CardHeader>
          <CardTitle>Search Stock History</CardTitle>
        </CardHeader>
        <CardContent>
          <Row className="align-items-center g-2">
            <Col xs="auto" style={{ minWidth: '150px' }}>
              <Dropdown>
                <Dropdown.Toggle variant="outline-dark" className="w-100">
                  {SEARCH_TYPES.find((type) => type.value === searchBy)?.label || 'Search By'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {SEARCH_TYPES.map((type) => (
                    <Dropdown.Item
                      key={type.value}
                      onClick={() => setSearchBy(type.value)}
                      active={searchBy === type.value}
                    >
                      {type.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col>
              <Input
                ref={searchRef}
                placeholder={`Enter ${SEARCH_TYPES.find((type) => type.value === searchBy)?.label || 'search value'}`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="w-100"
                type={searchBy === 'productId' ? 'number' : 'text'}
              />
            </Col>

            <Col xs="auto">
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={loading || !searchValue.trim()}
                className="d-flex align-items-center"
                style={{ gap: '8px' }}
              >
                <Search style={{ width: '16px', height: '16px' }} />
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </Col>
          </Row>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <Card>
        <CardContent className="p-0">
          <Nav variant="tabs" className="border-bottom-0">
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'search'}
                onClick={() => setActiveTab('search')}
                className="text-decoration-none"
              >
                Search History
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'inbound'}
                onClick={() => setActiveTab('inbound')}
                className="text-decoration-none"
              >
                Inbound Stock
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'outbound'}
                onClick={() => setActiveTab('outbound')}
                className="text-decoration-none"
              >
                Outbound Stock
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </CardContent>
      </Card>

      {/* Inbound Form */}
      {activeTab === 'inbound' && (
        <Card>
          <CardHeader>
            <CardTitle>Stock Inbound Operation</CardTitle>
          </CardHeader>
          <CardContent>
            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>SKU *</Form.Label>
                    <Form.Control
                      type="text"
                      value={inboundForm.sku}
                      onChange={(e) => setInboundForm((prev) => ({ ...prev, sku: e.target.value }))}
                      placeholder="Enter SKU"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Quantity *</Form.Label>
                    <Form.Control
                      type="number"
                      value={inboundForm.qty || ''}
                      onChange={(e) => setInboundForm((prev) => ({ ...prev, qty: parseInt(e.target.value) || 0 }))}
                      placeholder="Enter Quantity"
                      min="1"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tracking Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={inboundForm.trackingNumber || ''}
                      onChange={(e) => setInboundForm((prev) => ({ ...prev, trackingNumber: e.target.value }))}
                      placeholder="Enter Tracking Number (optional)"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Carrier</Form.Label>
                    <Form.Control
                      type="text"
                      value={inboundForm.carrier || ''}
                      onChange={(e) => setInboundForm((prev) => ({ ...prev, carrier: e.target.value }))}
                      placeholder="Enter Carrier (optional)"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Order ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={inboundForm.orderId || ''}
                      onChange={(e) => setInboundForm((prev) => ({ ...prev, orderId: e.target.value }))}
                      placeholder="Enter Order ID (optional)"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button
                variant="success"
                onClick={handleInboundSubmit}
                disabled={operationLoading || !inboundForm.sku.trim() || !inboundForm.qty}
                className="me-2"
              >
                {operationLoading ? 'Processing...' : 'Submit Inbound'}
              </Button>
            </Form>
          </CardContent>
        </Card>
      )}

      {/* Outbound Form */}
      {activeTab === 'outbound' && (
        <Card>
          <CardHeader>
            <CardTitle>Stock Outbound Operation</CardTitle>
          </CardHeader>
          <CardContent>
            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>SKU *</Form.Label>
                    <Form.Control
                      type="text"
                      value={outboundForm.sku}
                      onChange={(e) => setOutboundForm((prev) => ({ ...prev, sku: e.target.value }))}
                      placeholder="Enter SKU"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Quantity *</Form.Label>
                    <Form.Control
                      type="number"
                      value={outboundForm.qty || ''}
                      onChange={(e) => setOutboundForm((prev) => ({ ...prev, qty: parseInt(e.target.value) || 0 }))}
                      placeholder="Enter Quantity"
                      min="1"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tracking Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={outboundForm.trackingNumber || ''}
                      onChange={(e) => setOutboundForm((prev) => ({ ...prev, trackingNumber: e.target.value }))}
                      placeholder="Enter Tracking Number (optional)"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Carrier</Form.Label>
                    <Form.Control
                      type="text"
                      value={outboundForm.carrier || ''}
                      onChange={(e) => setOutboundForm((prev) => ({ ...prev, carrier: e.target.value }))}
                      placeholder="Enter Carrier (optional)"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={outboundForm.status || OutboundStatus.PENDING}
                      onChange={(e) =>
                        setOutboundForm((prev) => ({ ...prev, status: e.target.value as OutboundStatus }))
                      }
                    >
                      <option value={OutboundStatus.PENDING}>PENDING</option>
                      <option value={OutboundStatus.CONFIRMED}>CONFIRMED</option>
                      <option value={OutboundStatus.INBOUND_CANCEL}>INBOUND_CANCEL</option>
                      <option value={OutboundStatus.OUTBOUND_CANCEL}>OUTBOUND_CANCEL</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Order ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={outboundForm.orderId || ''}
                      onChange={(e) => setOutboundForm((prev) => ({ ...prev, orderId: e.target.value }))}
                      placeholder="Enter Order ID (optional)"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button
                variant="warning"
                onClick={handleOutboundSubmit}
                disabled={operationLoading || !outboundForm.sku.trim() || !outboundForm.qty}
                className="me-2"
              >
                {operationLoading ? 'Processing...' : 'Submit Outbound'}
              </Button>
            </Form>
          </CardContent>
        </Card>
      )}

      {/* Stock Status Section */}
      {activeTab === 'search' && hasSearched && stockData && (
        <Card>
          <CardHeader>
            <CardTitle>Stock Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 p-3 bg-light rounded">
              <div className="row mb-3">
                <div className="col-md-4">
                  <strong>Total Quantity:</strong>
                  <span className="ms-2 text-primary">{stockData.stockStatus.statistic.totalQuantity}</span>
                </div>
                <div className="col-md-4">
                  <strong>Reserved Quantity:</strong>
                  <span className="ms-2 text-warning">{stockData.stockStatus.statistic.totalReservedQuantity}</span>
                </div>
                <div className="col-md-4">
                  <strong>Available Quantity:</strong>
                  <span className="ms-2 text-success">
                    {stockData.stockStatus.statistic.totalQuantity -
                      stockData.stockStatus.statistic.totalReservedQuantity}
                  </span>
                </div>
              </div>

              {stockData.stockStatus.statistic.byLocation.length > 0 && (
                <div>
                  <strong>By Location:</strong>
                  <div className="mt-2">
                    {stockData.stockStatus.statistic.byLocation.map((location, index) => (
                      <div key={index} className="d-inline-block me-3 mb-2">
                        <Badge bg="secondary" className="me-1">
                          {location.locationId}
                        </Badge>
                        <span className="small">
                          Qty: {location.quantity} | Reserved: {location.reservedOutbound} | Available:{' '}
                          <span className="text-success">{location.quantity - location.reservedOutbound}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product Information Section */}
      {activeTab === 'search' && hasSearched && productInfo && (
        <Card>
          <CardHeader>
            <CardTitle>Product Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 p-3 bg-light rounded">
              <Row className="align-items-center">
                <Col md={3}>
                  {productInfo.productImageUrls && productInfo.productImageUrls.length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {productInfo.productImageUrls.slice(0, 2).map((imageUrl, index) => (
                        <img
                          key={index}
                          src={imageUrl}
                          alt={productInfo.name}
                          className="img-thumbnail"
                          style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                        />
                      ))}
                      {productInfo.productImageUrls.length > 2 && (
                        <div
                          className="img-thumbnail d-flex align-items-center justify-content-center text-muted"
                          style={{ width: '80px', height: '80px' }}
                        >
                          <small>+{productInfo.productImageUrls.length - 2}</small>
                        </div>
                      )}
                    </div>
                  )}
                </Col>
                <Col md={9}>
                  <div className="mb-2">
                    <strong>Product Name:</strong>
                    <h5 className="mt-1 mb-2">{productInfo.name}</h5>
                  </div>
                  <Row className="mb-2">
                    <Col md={6}>
                      <div className="mb-2">
                        <strong>SKU:</strong>
                        <span className="ms-2 font-monospace">{productInfo.sku}</span>
                      </div>
                      <div className="mb-2">
                        <strong>Product ID:</strong>
                        <span className="ms-2">{productInfo.productId}</span>
                      </div>
                      {productInfo.brand && (
                        <div className="mb-2">
                          <strong>Brand:</strong>
                          <span className="ms-2">{productInfo.brand}</span>
                        </div>
                      )}
                    </Col>
                    <Col md={6}>
                      {productInfo.size && (
                        <div className="mb-2">
                          <strong>Size:</strong>
                          <span className="ms-2">{productInfo.size}</span>
                        </div>
                      )}
                      {productInfo.pack && (
                        <div className="mb-2">
                          <strong>Pack:</strong>
                          <span className="ms-2">{productInfo.pack}</span>
                        </div>
                      )}
                      {productInfo.status && (
                        <div className="mb-2">
                          <strong>Status:</strong>
                          <Badge
                            bg={
                              productInfo.status === 'instock'
                                ? 'success'
                                : productInfo.status === 'outofstock'
                                  ? 'danger'
                                  : 'warning'
                            }
                            className="ms-2"
                          >
                            {productInfo.status}
                          </Badge>
                        </div>
                      )}
                    </Col>
                  </Row>
                  {productInfo.category && productInfo.category.length > 0 && (
                    <div className="mb-2">
                      <strong>Categories:</strong>
                      <div className="mt-1">
                        {productInfo.category.map((cat, index) => (
                          <Badge key={index} bg="light" text="dark" className="me-1">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {(productInfo.originalPrice || productInfo.salePrice) && (
                    <div className="mb-2">
                      <strong>Price:</strong>
                      {productInfo.salePrice && (
                        <span className="ms-2 text-success fw-bold">
                          {productInfo.currency} {productInfo.salePrice}
                        </span>
                      )}
                      {productInfo.originalPrice && productInfo.originalPrice !== productInfo.salePrice && (
                        <span className="ms-2 text-muted text-decoration-line-through">
                          {productInfo.currency} {productInfo.originalPrice}
                        </span>
                      )}
                    </div>
                  )}
                  {productInfo.productLink && (
                    <div className="mb-2">
                      <strong>Product Link:</strong>
                      <a
                        href={productInfo.productLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-2 text-primary"
                      >
                        View Product Page
                      </a>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Section */}
      {activeTab === 'search' && hasSearched && (
        <Card>
          <CardHeader>
            <CardTitle>Stock History Events</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading stock history...</p>
              </div>
            ) : stockData && stockData.events.length > 0 ? (
              <div>
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-dark">
                      <tr>
                        <th>Type</th>
                        <th>Quantity</th>
                        <th>Status</th>
                        <th>Tracking No</th>
                        <th>Carrier</th>
                        <th>Location</th>
                        <th>Order ID</th>
                        <th>Created At</th>
                        <th>Auditor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockData.events.map((event, index) => (
                        <tr key={index}>
                          <td>
                            <Badge bg={getTypeBadgeVariant(event.type)}>{event.type}</Badge>
                          </td>
                          <td className="text-center">
                            <span className={`badge ${event.type === 'INBOUND' ? 'bg-success' : 'bg-warning'}`}>
                              {event.type === 'INBOUND' ? '+' : '-'}
                              {event.qty}
                            </span>
                          </td>
                          <td>
                            <Badge bg={getStatusBadgeVariant(event.status)}>{event.status}</Badge>
                          </td>
                          <td className="font-monospace">{event.trackingNumber || '-'}</td>
                          <td>{event.carrier || '-'}</td>
                          <td>{event.locationId || '-'}</td>
                          <td>
                            {event.orderId ? (
                              <span
                                className="text-info"
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => handleOrderClick(event.orderId!)}
                              >
                                {event.orderId}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="text-nowrap">{formatDateTime(event.createdAt)}</td>
                          <td>{event.auditor}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="mt-3 text-muted">
                  <small>Found {stockData.events.length} stock event(s)</small>
                  {stockData.nextToken && (
                    <span className="ms-2 text-info">• More data available (pagination not implemented)</span>
                  )}
                </div>
              </div>
            ) : (
              activeTab === 'search' &&
              hasSearched && (
                <div className="text-center py-8">
                  <div className="text-muted">
                    <p>No stock history found</p>
                    <small>Try searching with a different product ID</small>
                  </div>
                </div>
              )
            )}
          </CardContent>
        </Card>
      )}

      {/* Help Section */}
      {activeTab === 'search' && (
        <Card>
          <CardContent className="py-3">
            <div className="text-muted">
              <small>
                <strong>How to use:</strong>
                <ul className="mb-0 mt-1">
                  <li>Select search type from dropdown: SKU (default) or Product ID</li>
                  <li>Enter a SKU or numeric Product ID in the search field</li>
                  <li>Click Search or press Enter</li>
                  <li>View stock status and history events</li>
                  <li>Click on Order ID to view order details in OMS</li>
                </ul>
              </small>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
